import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User, LogOut, Home, MessageSquare, Lock, Menu, X } from 'lucide-react';
import ChangePasswordModal from './ChangePasswordModal';
import toast from 'react-hot-toast';

const MemberNavbar = ({ member, onLogout, onPasswordChanged }) => {
  const location = useLocation();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/member/dashboard', icon: Home },
    { name: 'Chatbot', href: '/member/chatbot', icon: MessageSquare },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  const handlePasswordChanged = (updatedMember) => {
    setShowChangePassword(false);
    if (onPasswordChanged) onPasswordChanged(updatedMember);
    toast.success('Password changed successfully!');
  };

  return (
    <>
      <nav className="sticky top-0 z-40 bg-white shadow-sm border-b border-slate-200">
        <div className="container mx-auto px-4">
          <div className="flex justify-between h-16">
            <div className="flex items-center min-w-0">
              <div className="flex-shrink-0 flex items-center mr-2 sm:mr-6">
                <h1 className="text-lg sm:text-xl font-bold text-slate-900">Member portal</h1>
              </div>

              <div className="hidden sm:flex sm:space-x-6">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-16 ${
                        isActive(item.href)
                          ? 'border-primary-500 text-slate-900'
                          : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center gap-1 sm:gap-3">
              <div className="hidden md:flex items-center space-x-2 text-sm text-slate-700">
                <User className="w-4 h-4" />
                <span className="truncate max-w-[10rem]">{member?.name || 'Member'}</span>
                <span className="text-slate-400">|</span>
                <span className="text-xs text-slate-500">UAN: {member?.uan || ''}</span>
              </div>
              <button
                type="button"
                onClick={() => setShowChangePassword(true)}
                className="inline-flex items-center px-2 sm:px-3 py-2 text-sm text-slate-500 hover:text-slate-700 rounded-md"
                title="Change Password"
              >
                <Lock className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Change Password</span>
              </button>
              <button
                type="button"
                onClick={onLogout}
                className="inline-flex items-center px-2 sm:px-3 py-2 text-sm text-slate-500 hover:text-slate-700 rounded-md"
                title="Logout"
              >
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </button>
              <button
                type="button"
                className="sm:hidden p-2 text-slate-600 rounded-md hover:bg-slate-100"
                onClick={() => setMenuOpen((v) => !v)}
                aria-label="Menu"
              >
                {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {menuOpen && (
            <div className="sm:hidden border-t border-slate-100 py-2 space-y-1 pb-3">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                      isActive(item.href)
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.name}
                  </Link>
                );
              })}
              <div className="px-3 pt-1 text-xs text-slate-500">
                {member?.name} · UAN {member?.uan || '—'}
              </div>
            </div>
          )}
        </div>
      </nav>

      {showChangePassword && member && (
        <ChangePasswordModal
          member={member}
          onPasswordChanged={handlePasswordChanged}
          onClose={() => setShowChangePassword(false)}
        />
      )}
    </>
  );
};

export default MemberNavbar;
